import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Trash2, Users, LogOut, ArrowLeft, Mail } from "lucide-react";
import { ReportsManager } from "@/components/admin/ReportsManager";
import { SecurityMfa } from "@/components/admin/SecurityMfa";
import { ActivityLog } from "@/components/admin/ActivityLog";

export default function Admin() {
  const [list, setList] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/whitelist");
      setList(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/whitelist", { email, name });
      toast.success(`${email} approved`);
      setEmail(""); setName("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const remove = async () => {
    const target = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/admin/whitelist/${encodeURIComponent(target)}`);
      toast.success(`${target} removed`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <span className="font-display font-bold text-sm tracking-tight">ADMIN CONSOLE</span>
          <span className="font-num text-xs text-slate-400 hidden sm:inline">{user?.email}</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              data-testid="admin-view-briefing-button"
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary px-2 py-1.5"
            >
              <ArrowLeft className="w-4 h-4" /> View briefing
            </button>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-destructive px-2 py-1.5"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Approved viewers</h1>
        <p className="text-slate-500 mt-1">Only these email addresses can request a login code.</p>

        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 mt-6">
          <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium text-slate-700">Email address</label>
              <Input
                data-testid="admin-email-input"
                type="email"
                required
                placeholder="viewer@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div className="flex-1 w-full">
              <label className="text-sm font-medium text-slate-700">Name (optional)</label>
              <Input
                data-testid="admin-name-input"
                placeholder="Jane Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <Button
              data-testid="admin-add-button"
              type="submit"
              disabled={adding}
              className="h-11 bg-primary hover:bg-primary/90 w-full sm:w-auto"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Approve</>}
            </Button>
          </form>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl mt-6 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-200 bg-slate-50">
            <Users className="w-4 h-4 text-slate-500" />
            <span className="font-num text-xs uppercase tracking-wider text-slate-600">
              {list ? `${list.length} approved ${list.length === 1 ? "viewer" : "viewers"}` : "Loading…"}
            </span>
          </div>
          {list === null ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : list.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">No approved emails yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="admin-whitelist">
              {list.map((row) => (
                <li key={row.email} className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50" data-testid={`whitelist-row-${row.email}`}>
                  <Mail className="w-4 h-4 text-slate-300 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-num text-sm text-slate-800 truncate">{row.email}</div>
                    {row.name && <div className="text-xs text-slate-400 truncate">{row.name}</div>}
                  </div>
                  <button
                    onClick={() => setToDelete(row.email)}
                    data-testid={`whitelist-delete-${row.email}`}
                    className="ml-auto text-slate-300 hover:text-destructive p-1.5 rounded-md hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <ReportsManager />

        <SecurityMfa />

        <ActivityLog />
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-slate-700">{toDelete}</span> will no longer be able to sign in to the briefing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-confirm-delete" className="bg-destructive hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
